import { z } from 'zod';
/**
 * Page templates for the marketing site.
 *
 * A template declares which fields a page has; the CMS renders an editor from
 * that declaration and the website renders markup from it. Adding a page is
 * therefore choosing a template and filling in fields — no markup involved.
 */
export const FieldTypeSchema = z.enum(['text', 'textarea', 'html', 'image', 'items', 'toggle']);
const HERO = [
    { key: 'eyebrow', label: 'Eyebrow (small label above the title)', type: 'text' },
    { key: 'title', label: 'Page title', type: 'text' },
    { key: 'lead', label: 'Intro paragraph', type: 'textarea' },
];
export const TEMPLATES = [
    {
        id: 'blocks',
        name: 'Flexible page',
        description: 'Build the page from modules you choose: text, images, team, cards, figures.',
        fields: [],
    },
    {
        id: 'landing',
        name: 'Landing page',
        description: 'The home page: headline, key figures, showcase images, feature cards and the enquiry form.',
        fields: [
            ...HERO,
            { key: 'ctaPrimary', label: 'Primary button text', type: 'text' },
            { key: 'ctaSecondary', label: 'Secondary button text', type: 'text' },
            {
                key: 'stats',
                label: 'Key figures',
                type: 'items',
                itemFields: [
                    { key: 'value', label: 'Figure', type: 'text' },
                    { key: 'label', label: 'Caption', type: 'text' },
                ],
            },
            {
                key: 'showcase',
                label: 'Showcase images',
                type: 'items',
                itemFields: [
                    { key: 'title', label: 'Caption title', type: 'text' },
                    { key: 'body', label: 'Caption text', type: 'text' },
                    { key: 'image', label: 'Image', type: 'image' },
                ],
            },
            {
                key: 'items',
                label: 'Feature cards',
                type: 'items',
                itemFields: [
                    { key: 'title', label: 'Card title', type: 'text' },
                    { key: 'body', label: 'Card text', type: 'textarea' },
                ],
            },
            // The animated "See it work" line/floor scenes are shown by default (so the
            // home page keeps them); turn this off on pages that don't want them.
            { key: 'showScenes', label: 'Show the animated line/floor scenes', type: 'toggle' },
            // Optional pricing table. Leave the plans empty and no pricing shows.
            { key: 'pricingEyebrow', label: 'Pricing — small label', type: 'text' },
            { key: 'pricingTitle', label: 'Pricing — heading', type: 'text' },
            { key: 'pricingLead', label: 'Pricing — intro paragraph', type: 'textarea' },
            {
                key: 'pricing',
                label: 'Pricing plans',
                type: 'items',
                itemFields: [
                    { key: 'name', label: 'Plan name', type: 'text' },
                    { key: 'price', label: 'Price (e.g. $49 or Custom)', type: 'text' },
                    { key: 'period', label: 'Period (e.g. /mo)', type: 'text' },
                    { key: 'tag', label: 'Tagline', type: 'text' },
                    { key: 'features', label: 'Features — one per line, **bold** allowed', type: 'textarea' },
                    { key: 'buttonText', label: 'Button text', type: 'text' },
                    { key: 'featured', label: 'Highlight this plan? (yes/no)', type: 'text' },
                    { key: 'color', label: 'Colour: teal / blue / violet / amber', type: 'text' },
                ],
            },
            // Optional "real detections" image gallery. Empty = nothing shown.
            { key: 'examplesEyebrow', label: 'Examples — small label', type: 'text' },
            { key: 'examplesTitle', label: 'Examples — heading', type: 'text' },
            { key: 'examplesLead', label: 'Examples — intro paragraph', type: 'textarea' },
            {
                key: 'examples',
                label: 'Example images (real detections)',
                type: 'items',
                itemFields: [
                    { key: 'title', label: 'Caption title', type: 'text' },
                    { key: 'body', label: 'Caption text', type: 'text' },
                    { key: 'image', label: 'Image', type: 'image' },
                ],
            },
            { key: 'safetyTitle', label: 'Closing block title', type: 'text' },
            { key: 'safetyBody', label: 'Closing block text', type: 'textarea' },
            // The form section always rendered, so it was the one part of the page
            // an author could not take off. Absent means shown, so existing pages
            // keep their form without being touched.
            { key: 'showEnquiry', label: 'Show the enquiry form section', type: 'toggle' },
            { key: 'enquiryTitle', label: 'Enquiry form title', type: 'text' },
            { key: 'enquiryBody', label: 'Enquiry form text', type: 'textarea' },
        ],
    },
    {
        id: 'text',
        name: 'Text page',
        description: 'A heading and a body of text. Good for about, pricing or legal pages.',
        fields: [
            ...HERO,
            { key: 'body', label: 'Body', type: 'html', help: 'Basic HTML is allowed: <p>, <h2>, <ul>, <a>, <strong>.' },
        ],
    },
    {
        id: 'features',
        name: 'Feature grid',
        description: 'A heading plus a grid of cards. Good for capabilities or benefits.',
        fields: [
            ...HERO,
            {
                key: 'items',
                label: 'Cards',
                type: 'items',
                itemFields: [
                    { key: 'title', label: 'Card title', type: 'text' },
                    { key: 'body', label: 'Card text', type: 'textarea' },
                ],
            },
        ],
    },
    {
        id: 'steps',
        name: 'Step-by-step',
        description: 'Alternating blocks of text and images. Good for explaining a process.',
        fields: [
            ...HERO,
            {
                key: 'items',
                label: 'Steps',
                type: 'items',
                itemFields: [
                    { key: 'title', label: 'Step title', type: 'text' },
                    { key: 'body', label: 'Step text', type: 'textarea' },
                    { key: 'image', label: 'Image', type: 'image' },
                ],
            },
        ],
    },
    {
        id: 'contact',
        name: 'Contact page',
        description: 'Company details alongside the enquiry form.',
        fields: [
            ...HERO,
            { key: 'body', label: 'Text above the form', type: 'textarea' },
        ],
    },
];
export const getTemplate = (id) => TEMPLATES.find((t) => t.id === id);
export const BLOCK_TYPES = [
    {
        id: 'heading',
        name: 'Heading',
        description: 'A section title with an optional intro paragraph.',
        fields: [
            { key: 'eyebrow', label: 'Small label above', type: 'text' },
            { key: 'title', label: 'Heading', type: 'text' },
            { key: 'lead', label: 'Intro paragraph', type: 'textarea' },
        ],
    },
    {
        id: 'text',
        name: 'Text',
        description: 'A block of formatted text.',
        fields: [{ key: 'body', label: 'Text', type: 'html' }],
    },
    {
        id: 'image',
        name: 'Image',
        description: 'A single image with an optional caption.',
        fields: [
            { key: 'image', label: 'Image', type: 'image' },
            { key: 'caption', label: 'Caption', type: 'text' },
        ],
    },
    {
        id: 'imageText',
        name: 'Image beside text',
        description: 'An image next to a paragraph. Good for explaining something visually.',
        fields: [
            { key: 'title', label: 'Title', type: 'text' },
            { key: 'body', label: 'Text', type: 'textarea' },
            { key: 'image', label: 'Image', type: 'image' },
            { key: 'side', label: 'Image on the left? (yes/no)', type: 'text', help: 'Type "left" to flip the image to the left.' },
        ],
    },
    {
        id: 'features',
        name: 'Cards',
        description: 'A grid of short cards. Good for capabilities or benefits.',
        fields: [
            {
                key: 'items',
                label: 'Cards',
                type: 'items',
                itemFields: [
                    { key: 'title', label: 'Card title', type: 'text' },
                    { key: 'body', label: 'Card text', type: 'textarea' },
                ],
            },
        ],
    },
    {
        id: 'team',
        name: 'Team',
        description: 'People with a photo, name and role.',
        fields: [
            {
                key: 'items',
                label: 'People',
                type: 'items',
                itemFields: [
                    { key: 'image', label: 'Photo', type: 'image' },
                    { key: 'title', label: 'Name', type: 'text' },
                    { key: 'role', label: 'Role', type: 'text' },
                    { key: 'body', label: 'Short bio', type: 'textarea' },
                ],
            },
        ],
    },
    {
        id: 'stats',
        name: 'Key figures',
        description: 'A row of numbers with captions.',
        fields: [
            {
                key: 'items',
                label: 'Figures',
                type: 'items',
                itemFields: [
                    { key: 'value', label: 'Figure', type: 'text' },
                    { key: 'label', label: 'Caption', type: 'text' },
                ],
            },
        ],
    },
    {
        id: 'cta',
        name: 'Call to action',
        description: 'A highlighted band with a heading and a button.',
        fields: [
            { key: 'title', label: 'Heading', type: 'text' },
            { key: 'body', label: 'Text', type: 'textarea' },
            { key: 'buttonText', label: 'Button text', type: 'text' },
            { key: 'buttonHref', label: 'Button link', type: 'text', help: 'e.g. /contact' },
        ],
    },
];
export const getBlockType = (id) => BLOCK_TYPES.find((b) => b.id === id);
// ── API ──────────────────────────────────────────────────
export const SitePageSchema = z.object({
    id: z.number().int().positive(),
    slug: z.string(),
    title: z.string(),
    description: z.string(),
    template: z.string(),
    content: z.record(z.unknown()),
    published: z.boolean(),
    showInNav: z.boolean(),
    navLabel: z.string().nullable(),
    sortOrder: z.number().int(),
    updatedBy: z.string().nullable(),
    updatedAt: z.string(),
});
export const CreateSitePageSchema = z.object({
    slug: z
        .string()
        .min(1)
        .max(80)
        .regex(/^[a-z0-9-]+$/, 'Use lowercase letters, numbers and hyphens only'),
    title: z.string().min(1).max(200),
    description: z.string().max(400).default(''),
    template: z.string().min(1),
    content: z.record(z.unknown()).default({}),
    published: z.boolean().default(false),
    showInNav: z.boolean().default(false),
    navLabel: z.string().max(60).nullable().optional(),
    sortOrder: z.number().int().default(0),
});
export const UpdateSitePageSchema = CreateSitePageSchema.partial();
//# sourceMappingURL=cms.js.map